import { useEffect, useMemo, useState } from "react";
import { AppButton, toast } from "../../components/ui";
import {
  AssignTaskRequest,
  ClioService,
  MatterPreviewResponse,
  TaskPreviewResponse,
} from "../../api";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import { ClioMatterAutocomplete } from "../../components/shared/MatterAutocomplete";
import { ClioUserSelect } from "../../components/shared/ClioSelects";

type Props = {
  onAssigned: () => void;
};

export default function AssignTaskForm({ onAssigned }: Props) {
  const [matterValues, setMatterValues] = useState<MatterPreviewResponse[]>([]);
  const [taskInputValue, setTaskInputValue] = useState("");
  const [taskValue, setTaskValue] = useState<TaskPreviewResponse | null>(null);
  const [taskOptions, setTaskOptions] = useState<readonly TaskPreviewResponse[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setMatterValues([]);
    setTaskValue(null);
    setTaskInputValue("");
    setTaskOptions([]);
    setSelectedUser(null);
  };

  const fetchTasks = useMemo(
    () =>
      debounce((input: string) => {
        ClioService.getApiClioTaskTypes(input).then((response) => {
          const newOptions: TaskPreviewResponse[] = taskValue ? [taskValue] : [];
          if (response) {
            newOptions.push(...response);
          }
          setTaskOptions(newOptions);
        });
      }, 200),
    [taskValue]
  );

  useEffect(() => {
    if (taskInputValue === "") {
      setTaskOptions(taskValue ? [taskValue] : []);
      return;
    }
    fetchTasks(taskInputValue);
  }, [taskValue, taskInputValue, fetchTasks]);

  const matterIds = matterValues
    .map((m) => m.id)
    .filter((id): id is number => id != null);

  const canSubmit =
    matterIds.length > 0 && taskValue?.id != null && selectedUser != null;

  const assignTask = () => {
    if (!canSubmit) {
      toast.warning("Please complete all required fields.");
      return;
    }

    const request = {
      matterIds,
      taskTypeId: +taskValue!.id!,
      assigneeId: selectedUser!,
    } as AssignTaskRequest;

    setSubmitting(true);
    ClioService.postApiClioAssignTask(request)
      .then(() => {
        toast.success(
          matterIds.length === 1
            ? "Task assigned successfully."
            : `Task assigned to ${matterIds.length} matters.`
        );
        onAssigned();
      })
      .catch(() => toast.error("Error assigning task."))
      .finally(() => setSubmitting(false));
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Assign Clio Task
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Select one or more matters, then choose the same task and assignee for
          all of them.
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ md: "flex-start" }}
      >
        <ClioMatterAutocomplete
          multiple
          value={matterValues}
          onChange={setMatterValues}
          label="Matters"
          required
          size="small"
          sx={{ flex: 1, minWidth: 0, width: "100%" }}
        />

        <Autocomplete
          sx={{ flex: 1, minWidth: 0, width: "100%" }}
          getOptionLabel={(option) =>
            (typeof option === "string" ? option : option.name) as string
          }
          filterOptions={(x) => x}
          options={taskOptions}
          autoComplete
          includeInputInList
          filterSelectedOptions
          value={taskValue}
          noOptionsText="Start typing task"
          onChange={(_, selectedValue) => setTaskValue(selectedValue)}
          onInputChange={(_, newInputValue) => setTaskInputValue(newInputValue)}
          renderInput={(params) => (
            <TextField {...params} label="Task" required fullWidth size="small" />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography variant="body2">{option.name}</Typography>
            </li>
          )}
        />

        <ClioUserSelect
          label="Assignee"
          fullWidth
          required
          includeAll={false}
          size="small"
          valueMode="id"
          removeDisabled
          value={selectedUser ?? undefined}
          onChange={(userId) => setSelectedUser(userId == null ? null : Number(userId))}
          sx={{ flex: 1, minWidth: 0, width: "100%" }}
        />
      </Stack>

      <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2.5 }}>
        <AppButton
          onClick={resetForm}
          variant="outlined"
          color="primary"
          disabled={submitting}
        >
          Clear
        </AppButton>
        <AppButton
          onClick={assignTask}
          variant="contained"
          color="primary"
          disabled={!canSubmit || submitting}
          startIcon={
            submitting ? <CircularProgress size={18} color="inherit" /> : undefined
          }
        >
          Assign Task{matterIds.length > 1 ? ` (${matterIds.length})` : ""}
        </AppButton>
      </Stack>
    </Paper>
  );
}
